import type { ComponentType, SVGProps } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { NEUTRAL_BUTTON_CLASS } from '../../details/CredsActionButtons';
import { ScanImageCarousel } from './ScanImageCarousel';
import { galleryStageClass, galleryThumbSizeClass } from './scanImageTabs';
import type { ScanImageSet } from './useScanImageSets';

const THUMB_SKELETON_COUNT = 5;
const FILL_CLASS = 'flex min-h-0 flex-1 flex-col';

function GallerySkeleton({ fullscreen }: { fullscreen: boolean }) {
    return (
        <div className={`flex flex-col gap-2 ${fullscreen ? FILL_CLASS : ''}`} aria-hidden="true">
            <div className={`${galleryStageClass(fullscreen)} animate-pulse`} />
            <div className="flex gap-1.5 py-0.5">
                {Array.from({ length: THUMB_SKELETON_COUNT }, (_, index) => (
                    <div
                        key={index}
                        className={`${galleryThumbSizeClass(fullscreen)} animate-pulse rounded-md bg-gray-100`}
                    />
                ))}
            </div>
        </div>
    );
}

type StageTone = 'muted' | 'warning' | 'error';

const TONE_TEXT_CLASS: Readonly<Record<StageTone, string>> = {
    muted: 'text-gray-500',
    warning: 'text-amber-700',
    error: 'text-red-600'
};

interface StageNoteProps {
    text: string;
    tone: StageTone;
    fullscreen: boolean;
    icon?: ComponentType<SVGProps<SVGSVGElement>>;
    onRetry?: () => void;
}

function StageNote({ text, tone, fullscreen, icon: Icon = PhotoIcon, onRetry }: StageNoteProps) {
    const textClass = TONE_TEXT_CLASS[tone];
    return (
        <div
            role={tone === 'error' ? 'alert' : undefined}
            className={`${galleryStageClass(fullscreen)} flex flex-col items-center justify-center gap-3 px-8 text-center`}
        >
            <Icon className="h-8 w-8 text-gray-400" strokeWidth={1.2} aria-hidden="true" />
            <p className={`max-w-[280px] text-[13px] ${textClass}`}>{text}</p>
            {onRetry && (
                <button type="button" onClick={onRetry} className={NEUTRAL_BUTTON_CLASS}>
                    <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Try again
                </button>
            )}
        </div>
    );
}

interface ScanImagesPanelProps {
    set: ScanImageSet;
    urls: readonly string[];
    index: number;
    fullscreen: boolean;
    onIndexChange: (index: number) => void;
    onEnlarge: () => void;
}

export function ScanImagesPanel({
    set,
    urls,
    index,
    fullscreen,
    onIndexChange,
    onEnlarge
}: ScanImagesPanelProps) {
    const { tab, view, retry } = set;
    switch (view.status) {
        case 'loading':
            return (
                <div aria-busy="true" className={fullscreen ? FILL_CLASS : undefined}>
                    <span className="sr-only">Loading {tab.label.toLowerCase()}</span>
                    <GallerySkeleton fullscreen={fullscreen} />
                </div>
            );
        case 'generating':
            return (
                <StageNote
                    text={tab.generatingText}
                    tone="warning"
                    fullscreen={fullscreen}
                    onRetry={retry}
                />
            );
        case 'empty':
            return <StageNote text={tab.emptyText} tone="muted" fullscreen={fullscreen} />;
        case 'error':
            return (
                <StageNote
                    text={view.message}
                    tone="error"
                    fullscreen={fullscreen}
                    icon={ExclamationTriangleIcon}
                    onRetry={retry}
                />
            );
        case 'ready':
            return (
                <ScanImageCarousel
                    names={view.images.map((image) => image.name)}
                    urls={urls}
                    index={index}
                    label={tab.label}
                    imageNoun={tab.imageNoun}
                    fullscreen={fullscreen}
                    onIndexChange={onIndexChange}
                    onEnlarge={fullscreen ? undefined : onEnlarge}
                />
            );
    }
}
